/**
 * Personal Dashboard
 *
 * Main dashboard for authenticated users featuring:
 * - Your Agent panel
 * - Your Work (verified contributions)
 * - Your Memory (portable artifacts)
 * - Agent suggestion panel
 * - Propose → simulate → gated outcome flow
 *
 * Governance surfaces are behind "System Details".
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Brain,
  History,
  FolderOpen,
  ShieldCheck,
  Lock,
  Github,
  Download,
  Settings,
  LogOut,
  ArrowRight,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { AgentProposalFlow } from './AgentProposalFlow';
import { PersonalizationQuiz, QuizAnswers } from './PersonalizationQuiz';
import { BestNextMovePanel } from './BestNextMovePanel';
import { GovernedChat } from './GovernedChat';
import { getCurrentStage, AgentSuggestion, DemoChange, SuggestionStage } from './agentSuggestions';
import { validatePlaygroundFork, readScenarioFiles, ScenarioData, generateXablePreview, XablePreview, scenarioToPath } from '../utils/validatePlaygroundFork';
import { generateSuggestionFromFork, ForkSuggestion } from '../utils/deterministicSuggestions';
import { useNavigate } from 'react-router-dom';

interface PersonalDashboardProps {
  onSignOut: () => void;
  isDemoMode?: boolean;
}

type TabType = 'agent' | 'work' | 'memory';

export const PersonalDashboard: React.FC<PersonalDashboardProps> = ({ onSignOut, isDemoMode }) => {
  const [activeTab, setActiveTab] = useState<TabType>('agent');
  const [showProposalFlow, setShowProposalFlow] = useState(false);
  const [hasGitHubConnected, setHasGitHubConnected] = useState(false);
  const [hasContributions, setHasContributions] = useState(false);
  const [hasMemory, setHasMemory] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswers | null>(null);
  const [quizPromptShown, setQuizPromptShown] = useState(false);

  // History state
  const [recentProposals, setRecentProposals] = useState<any[]>([]);
  const [contributions, setContributions] = useState<any[]>([]);
  const [memoryArtifacts, setMemoryArtifacts] = useState<any[]>([]);

  // Selected suggestion for proposal flow
  const navigate = useNavigate();
  const [selectedChange, setSelectedChange] = useState<DemoChange | null>(null);
  const [forkSuggestion, setForkSuggestion] = useState<ForkSuggestion | null>(null);
  const [isForkConnected, setIsForkConnected] = useState(false);

  // Current stage advances with each completed proposal
  const currentStage = getCurrentStage(recentProposals.length);

  // Playground fork connection state
  const [showForkModal, setShowForkModal] = useState(false);
  const [forkUrl, setForkUrl] = useState('');
  const [forkError, setForkError] = useState('');
  const [connectedFork, setConnectedFork] = useState<{ owner: string; repo: string } | null>(null);
  const [scenarioData, setScenarioData] = useState<ScenarioData | null>(null);
  const [loadingScenario, setLoadingScenario] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [xablePreview, setXablePreview] = useState<XablePreview | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('xableai_quiz_answers');
      if (saved) {
        const parsed = JSON.parse(saved) as QuizAnswers;
        // Validate structure
        if (parsed && parsed.intentType && parsed.workSource && parsed.firstGoal) {
          setQuizAnswers(parsed);
          console.log('[Quiz] Loaded from localStorage:', parsed);
        } else {
          // Corrupt data - clear it
          localStorage.removeItem('xableai_quiz_answers');
          console.warn('[Quiz] Corrupt data cleared from localStorage');
        }
      }
    } catch (e) {
      console.warn('[Quiz] Failed to load from localStorage:', e);
      // Clear corrupt data
      try {
        localStorage.removeItem('xableai_quiz_answers');
      } catch (cleanupError) {
        console.warn('[Quiz] Failed to clear corrupt localStorage:', cleanupError);
      }
    }
  }, []);

  // Show quiz prompt if no answers and not shown yet
  useEffect(() => {
    if (!quizAnswers && !quizPromptShown) {
      // Show prompt after a short delay
      const timer = setTimeout(() => {
        setShowQuiz(true);
        setQuizPromptShown(true);
        console.log('[Quiz] Prompt shown');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [quizAnswers, quizPromptShown]);

  const handleQuizComplete = (answers: QuizAnswers) => {
    setQuizAnswers(answers);
    setShowQuiz(false);
    console.log('[Quiz] Completed:', answers);
  };

  const handleEditQuiz = () => {
    setShowQuiz(true);
    console.log('[Quiz] Edit preferences clicked');
  };

  const handleExecutionComplete = (proposal: any, receipt: any) => {
    console.log('[Dashboard] Execution complete:', proposal, receipt);

    // Add to recent proposals
    setRecentProposals(prev => [{
      id: receipt.receiptId,
      title: proposal.title,
      status: 'executed',
      outcome: 'ALLOW',
      timestamp: receipt.timestamp
    }, ...prev]);

    // Add to contributions (Your Work)
    setContributions(prev => [{
      id: receipt.receiptId,
      title: proposal.title,
      file: receipt.governedObjectReference.file,
      timestamp: receipt.timestamp,
      receiptHash: receipt.receiptHash
    }, ...prev]);
    setHasContributions(true);

    // Add to memory artifacts (Your Memory)
    setMemoryArtifacts(prev => [{
      id: receipt.receiptId,
      type: 'receipt',
      title: proposal.title,
      hash: receipt.receiptHash,
      timestamp: receipt.timestamp
    }, ...prev]);
    setHasMemory(true);
  };

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] text-white">
      {/* Top Nav */}
      <nav className="border-b border-gray-800 bg-[#0a0a0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Brain className="w-6 h-6 text-purple-400" />
            <span className="text-xl font-bold">XableAI</span>
          </div>
          <div className="flex items-center space-x-4">
            <button className="text-gray-300 hover:text-white transition-colors">
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={onSignOut}
              className="text-gray-300 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold mb-2">Welcome back</h1>
            <p className="text-gray-400">Your AI workspace is ready.</p>
          </motion.div>

          {/* Best Next Move Panel - always visible above chat */}
          <BestNextMovePanel onOpenProposalFlow={(move) => { setSelectedChange(null); setShowProposalFlow(true); }} />

          {/* Governed Chat - always visible below Best Next Move */}
          <GovernedChat 
            onRouteToSurface={(surface) => {
              if (surface === '/signals') navigate('/signals');
              if (surface === '/verify') navigate('/verify');
            }}
            onOpenProposalFlow={() => { setSelectedChange(null); setShowProposalFlow(true); }}
          />

          {/* Tab Navigation */}
          <div className="flex space-x-4 mb-8 border-b border-gray-800">
            {[
              { id: 'agent' as TabType, label: 'Your Agent', icon: Brain },
              { id: 'work' as TabType, label: 'Your Work', icon: History },
              { id: 'memory' as TabType, label: 'Your Memory', icon: FolderOpen }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'agent' && <AgentTab recentProposals={recentProposals} />}
            {activeTab === 'work' && <WorkTab hasContributions={hasContributions} contributions={contributions} />}
            {activeTab === 'memory' && <MemoryTab hasMemory={hasMemory} memoryArtifacts={memoryArtifacts} />}
          </motion.div>
        </div>

        {/* Fork Connection UI */}
        {connectedFork && (
          <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Connected fork</h3>
              <span className="text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded-full border border-green-700">
                Connected
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-3">
              <span className="text-white">{connectedFork.owner}</span>/xable-playground
            </p>

            {/* Scenario Selection */}
            <div className="mb-3">
              <p className="text-xs text-gray-400 mb-2">Select a scenario to generate a suggestion:</p>
              <div className="space-y-1">
                {['React Dashboard Null Crash', 'API Validation Missing', 'Async State Race', 'Full-Stack Stabilization', 'Governed Workflow Simulation', 'Korros Governed Agent Challenge'].map((scenario) => (
                  <button
                    key={scenario}
                    onClick={async () => {
                      setLoadingScenario(true);
                      const scenarioPath = scenarioToPath(scenario);
                      if (connectedFork && scenarioPath) {
                        const result = await readScenarioFiles(connectedFork.owner, scenario);
                        if (result.success && result.data) {
                          setScenarioData(result.data);
                          const suggestion = generateSuggestionFromFork(result.data, scenarioPath.replace('scenarios/', ''));
                          setForkSuggestion(suggestion);
                          setSelectedChange(null); // Clear demo change
                          setShowProposalFlow(true);
                        }
                      }
                      setLoadingScenario(false);
                    }}
                    disabled={loadingScenario}
                    className="w-full text-left px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                  >
                    {scenario}
                  </button>
                ))}
              </div>
            </div>

            {generatingPreview ? (
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Generating .xable preview...</span>
              </div>
            ) : xablePreview ? (
              <div className="space-y-3">
                <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-2">.xable/ preview generated</p>
                  <div className="space-y-1 text-xs text-gray-300">
                    <div>• xable.project.json</div>
                    <div>• xable.summary.md</div>
                  </div>
                </div>

                {/* Phase 4: Patch Output Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      const previewText = `# .xable/ Preview

## xable.project.json
\`\`\`json
${xablePreview['xable.project.json']}
\`\`\`

## xable.summary.md
\`\`\`markdown
${xablePreview['xable.summary.md']}
\`\`\``;
                      navigator.clipboard.writeText(previewText);
                      console.log('[.xable] Copied to clipboard');
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                  >
                    <span>Copy .xable Patch</span>
                  </button>
                  <button
                    onClick={() => {
                      const previewText = `# .xable/ Preview

## xable.project.json
\`\`\`json
${xablePreview['xable.project.json']}
\`\`\`

## xable.summary.md
\`\`\`markdown
${xablePreview['xable.summary.md']}
\`\`\``;
                      const blob = new Blob([previewText], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'xable-preview.txt';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      console.log('[.xable] Downloaded');
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download .xable Folder</span>
                  </button>
                  <a
                    href={`https://github.com/${connectedFork.owner}/xable-playground`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                  >
                    <Github className="w-4 h-4" />
                    <span>Open Fork on GitHub</span>
                  </a>
                </div>

                {/* Phase 5: Security Copy */}
                <div className="p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <p className="text-xs text-blue-300">
                    Xable is reading only your public playground fork. It cannot commit, push, merge, or deploy. This preview is yours to copy or apply manually.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-400">
                Preview generation failed. Please try reconnecting.
              </div>
            )}
          </div>
        )}

        {/* Demo Mode Label */}
        {!isForkConnected && (
          <div className="bg-yellow-900/20 border border-yellow-700 rounded-xl p-4">
            <p className="text-xs text-yellow-300">
              Demo mode — connect your fork to run this on real code
            </p>
          </div>
        )}
      </div>

      {/* Agent Proposal Flow Modal */}
      <AgentProposalFlow
        isOpen={showProposalFlow}
        onClose={() => { setShowProposalFlow(false); setSelectedChange(null); setScenarioData(null); setForkSuggestion(null); }}
        onExecutionComplete={handleExecutionComplete}
        preselectedChange={selectedChange}
        scenarioData={scenarioData}
        forkSuggestion={forkSuggestion}
        isForkConnected={isForkConnected}
      />

      {/* Personalization Quiz */}
      <PersonalizationQuiz
        isOpen={showQuiz}
        onClose={() => setShowQuiz(false)}
        onComplete={handleQuizComplete}
        existingAnswers={quizAnswers}
      />

      {/* Fork Connection Modal */}
      <AnimatePresence>
        {showForkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowForkModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gray-900 border border-gray-700 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-semibold mb-2">Connect Your Fork</h2>
                <p className="text-gray-400 text-sm">Connect a fork of korzent/xable-playground to simulate real scenarios.</p>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Fork URL
                  </label>
                  <input
                    type="text"
                    value={forkUrl}
                    onChange={(e) => setForkUrl(e.target.value)}
                    placeholder="https://github.com/<owner>/xable-playground"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  />
                </div>
                {forkError && (
                  <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg">
                    <p className="text-sm text-red-400">{forkError}</p>
                  </div>
                )}
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowForkModal(false);
                      setForkUrl('');
                      setForkError('');
                    }}
                    className="flex-1 bg-gray-800 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setForkError('');
                      const result = await validatePlaygroundFork(forkUrl);
                      if (result.valid && result.repo) {
                        setConnectedFork({ owner: result.repo.owner, repo: result.repo.name });
                        setIsForkConnected(true);
                        setGeneratingPreview(true);
                        const previewResult = await generateXablePreview(result.repo.owner, result.repo.url);
                        if (previewResult.success && previewResult.preview) {
                          setXablePreview(previewResult.preview);
                        }
                        setGeneratingPreview(false);
                        setShowForkModal(false);
                        setForkUrl('');
                      } else {
                        setForkError(result.error || 'Failed to validate fork');
                      }
                    }}
                    disabled={!forkUrl}
                    className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Connect
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const AgentTab: React.FC<{ recentProposals: any[] }> = ({ recentProposals }) => {
  return (
    <div className="space-y-6">
      {/* Recent Proposals */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Proposals</h2>
        {recentProposals.length === 0 ? (
          <p className="text-gray-500 text-sm">No proposals yet. Use Best Next Move above to create your first proposal.</p>
        ) : (
          <div className="space-y-3">
            {recentProposals.map((proposal) => (
              <div key={proposal.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{proposal.title}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    proposal.outcome === 'ALLOW'
                      ? 'bg-green-900/30 text-green-400 border border-green-700'
                      : 'bg-red-900/30 text-red-400 border border-red-700'
                  }`}>
                    {proposal.outcome}
                  </span>
                </div>
                <div className="text-sm text-gray-500">{new Date(proposal.timestamp).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WorkTab: React.FC<{ hasContributions: boolean; contributions: any[] }> = ({ hasContributions, contributions }) => {
  if (!hasContributions) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-700 rounded-xl p-8 text-center">
          <History className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">No Verified Contributions Yet</h2>
          <p className="text-gray-400 mb-6">
            Connect GitHub to import your work history and start building your verified contributions.
          </p>
          <button className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors">
            Connect GitHub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Contributions List */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Your Verified Contributions</h2>
        {contributions.length === 0 ? (
          <p className="text-gray-500 text-sm">No contributions yet. Create a proposal to review your first generated patch.</p>
        ) : (
          <div className="space-y-3">
            {contributions.map((contribution) => (
              <div key={contribution.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <span className="text-white font-medium">{contribution.title}</span>
                    <div className="text-xs text-purple-400 font-mono mt-1">{contribution.file}</div>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                  <span>{new Date(contribution.timestamp).toLocaleString()}</span>
                  <span className="text-purple-400">Added to your verified work history</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MemoryTab: React.FC<{ hasMemory: boolean; memoryArtifacts: any[] }> = ({ hasMemory, memoryArtifacts }) => {
  const handleExportBundle = () => {
    // Create a sample personal AI bundle
    const bundle = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      owner: 'user-principal-hash',
      artifacts: {
        contributions: memoryArtifacts.length,
        memoryArtifacts: memoryArtifacts.length,
        memorySize: '45 MB'
      },
      portableData: {
        contributionHistory: 'contribution-history.json',
        aiMemoryArtifacts: 'ai-memory-artifacts.json',
        verifiedReceipts: 'verified-receipts.json'
      }
    };

    // Create a blob and trigger download
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `xableai-personal-bundle-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!hasMemory) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-700 rounded-xl p-8 text-center">
          <FolderOpen className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Your Memory Builds Over Time</h2>
          <p className="text-gray-400 mb-6">
            As you work and connect GitHub, your AI memory will grow with your verified contributions and artifacts.
          </p>
          <button className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors">
            Connect GitHub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Memory Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { label: 'Artifacts', value: memoryArtifacts.length },
          { label: 'Contributions', value: memoryArtifacts.length },
          { label: 'Memory Size', value: '45 MB' }
        ].map((stat, index) => (
          <div key={index} className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-purple-400 mb-2">{stat.value}</p>
            <p className="text-gray-400 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Portable Artifacts */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Portable Memory</h2>
          <button onClick={handleExportBundle} className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all">
            <Download className="w-4 h-4" />
            <span>Export Bundle</span>
          </button>
        </div>
        {memoryArtifacts.length === 0 ? (
          <p className="text-gray-500 text-sm">No artifacts yet. Create a proposal to review your first generated patch.</p>
        ) : (
          <div className="space-y-3">
            {memoryArtifacts.map((artifact) => (
              <div key={artifact.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FolderOpen className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-white font-medium">{artifact.title}</p>
                    <p className="text-gray-400 text-sm capitalize">{artifact.type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-purple-400 text-xs font-mono">{artifact.hash}</p>
                  <div className="flex items-center space-x-1 text-green-400 text-sm mt-1">
                    <CheckCircle className="w-3 h-3" />
                    <span>Verifiable</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
